/**
 * DevRoleSwitcher — header control to assume another role while testing.
 *
 * Non-production environments only (same gate as the header EnvironmentBadge).
 * Real-auth mode: POST /api/dev/assume-role overwrites the caller's own
 * dbo.users.role (the endpoint has no Admin gate, so switching back from
 * Viewer/Manager/Operator always works), then reloads so nav/guards re-derive.
 * DEV_AUTH_BYPASS mode: switches the local dev persona instead (no DB row).
 */
import React from 'react';
import { Select } from '@/components/ui';
import { AppTooltip } from '@/components/ui/AppTooltip';
import { apiClient } from '../../modules/backup/services/api';
import { isDevAuthBypass } from '../../authConfig';
import { setDevUserRole } from '../../auth/devUserStore';
import { useAuth } from '../../auth/hooks/useAuth';
import { useEnvironment } from '../../modules/backup/hooks/useEnvironment';

// Spec 285: SuperAdmin is an enforced role, so it must be pickable in dev.
const ROLES = ['SuperAdmin', 'Admin', 'Manager', 'Operator', 'Viewer'] as const;

export const DevRoleSwitcher: React.FC = () => {
  const { user } = useAuth();
  const { environmentInfo } = useEnvironment();
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  // Same gate as the EnvironmentBadge: render only once the server has
  // confirmed a non-production environment.
  if (!environmentInfo || environmentInfo.environment === 'production' || !user) {
    return null;
  }

  const currentRole =
    ROLES.find((r) => r.toLowerCase() === String(user.role ?? '').toLowerCase()) ?? '';

  const assumeRole = async (role: string) => {
    if (!role || role === currentRole || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      if (isDevAuthBypass) {
        setDevUserRole(role);
      } else {
        await apiClient.post('/api/dev/assume-role', { role });
      }
      // Full reload re-runs AuthProvider (/api/auth/session + permissions) so
      // nav items, route guards, and dashboards all re-derive from the new role.
      window.location.reload();
    } catch (error) {
      console.error('[DevRoleSwitcher] assume-role failed', error);
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <AppTooltip title='Dev only: assume a role to test its views. Overwrites your user role; switch back any time.'>
      <div className='hidden sm:block'>
        <Select
          aria-label='Assume role (dev only)'
          selectSize='sm'
          value={currentRole}
          disabled={busy}
          error={failed}
          onChange={(e) => void assumeRole(e.target.value)}
          options={ROLES.map((r) => ({ value: r, label: `View as: ${r}` }))}
        />
      </div>
    </AppTooltip>
  );
};

export default DevRoleSwitcher;
